import { createBrowserRouter, Navigate } from 'react-router'
import type { RouteObject } from 'react-router'
import { AppShell } from './layout/AppShell'
import { Ayuda } from './screens/Ayuda/Ayuda'
import { Conversacion } from './screens/Ayuda/Conversacion'
import { NuevaPeticion } from './screens/Ayuda/NuevaPeticion'
import { Peticion } from './screens/Ayuda/Peticion'
import { Accesos } from './screens/Accesos/Accesos'
import { Clima } from './screens/Accesos/Clima'
import { Codigo } from './screens/Accesos/Codigo'
import { ComoLlegar } from './screens/Accesos/ComoLlegar'
import { Instrucciones } from './screens/Accesos/Instrucciones'
import { CheckinFlow, CheckinStep, Completar, Ocupantes } from './screens/Checkin/Checkin'
import { Avisos } from './screens/Cierre/Avisos'
import { Despues } from './screens/Cierre/Despues'
import { Perfil } from './screens/Cierre/Perfil'
import { Salida } from './screens/Cierre/Salida'
import { Estancia } from './screens/Estancia/Estancia'
import { ExtrasCatalogo } from './screens/Extras/Catalogo'
import { ExtrasConfirmar } from './screens/Extras/Confirmar'
import { ExtrasListo } from './screens/Extras/Listo'
import { Fianza } from './screens/Fianza/Fianza'
import { Guia } from './screens/Guia/Guia'
import { ReservarFianza } from './screens/Fianza/ReservarFianza'
import { Autorizar } from './screens/Pagos/Autorizar'
import { PagoPendiente } from './screens/Pagos/PagoPendiente'
import { Recibos } from './screens/Pagos/Recibos'
import { Cancelar } from './screens/Reserva/Cancelar'
import { CambiarElegir } from './screens/Reserva/Elegir'
import { CambiarListo } from './screens/Reserva/Listo'
import { NoDisponible } from './screens/Reserva/NoDisponible'
import { CambiarPagar } from './screens/Reserva/Pagar'
import { CambiarPrecio } from './screens/Reserva/Precio'
import { Reserva } from './screens/Reserva/Reserva'
import { SinPago } from './screens/Reserva/SinPago'
import { Tarjeta } from './screens/Pagos/Tarjeta'
import { Tasa } from './screens/Pagos/Tasa'
import { TabPlaceholder } from './screens/TabPlaceholder/TabPlaceholder'
import { featureEnabled } from '@/config/features'

/**
 * Rutas de desarrollo: playground del sistema (/ds, /ds/frame) y banco simulado del mock de pagos (/dev/banco).
 * Solo existen con `import.meta.env.DEV` y se cargan con import() dinámico: en el build de producción la rama es
 * código muerto y ni las rutas ni sus módulos llegan al bundle (prueba en app/__tests__/devRoutes.ts).
 */
export const DEV_ROUTE_PATHS = ['/ds', '/ds/frame', '/dev/banco'] as const

function DisabledFeatureRoute() {
  return <Navigate to={{ pathname: '/ayuda', search: window.location.search }} replace />
}

const devRoutes: RouteObject[] = import.meta.env.DEV
  ? [
      { path: '/ds', lazy: () => import('./screens/Playground/Playground').then((m) => ({ Component: m.Playground })) },
      { path: '/ds/frame', lazy: () => import('./screens/Playground/PlaygroundFrame').then((m) => ({ Component: m.PlaygroundFrame })) },
      { path: '/dev/banco', lazy: () => import('./screens/DevBanco/DevBanco').then((m) => ({ Component: m.DevBanco })) },
    ]
  : []

export const routes: RouteObject[] = [
  ...devRoutes,
  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, element: <Estancia /> },
      { path: 'reserva/pagos', element: <PagoPendiente /> },
      { path: 'reserva/pagos/autorizar', element: <Autorizar /> },
      { path: 'reserva/pagos/tasa', element: <Tasa /> },
      { path: 'reserva/pagos/tarjeta', element: <Tarjeta /> },
      { path: 'reserva/fianza', element: <Fianza /> },
      { path: 'reserva/fianza/reservar', element: <ReservarFianza /> },
      { path: 'reserva/recibos', element: <Recibos /> },
      { path: 'reserva/extras', element: <ExtrasCatalogo /> },
      { path: 'reserva/extras/confirmar', element: <ExtrasConfirmar /> },
      { path: 'reserva/extras/listo', element: <ExtrasListo /> },
      { path: 'reserva', element: <Reserva /> },
      { path: 'reserva/cancelar', element: <Cancelar /> },
      { path: 'reserva/cancelar/no-disponible', element: <NoDisponible /> },
      { path: 'reserva/cambiar/:tipo', element: <CambiarElegir /> },
      { path: 'reserva/cambiar/:tipo/precio', element: <CambiarPrecio /> },
      { path: 'reserva/cambiar/:tipo/pagar', element: <CambiarPagar /> },
      { path: 'reserva/cambiar/:tipo/listo', element: <CambiarListo /> },
      { path: 'reserva/cambiar/:tipo/no-disponible', element: <NoDisponible /> },
      { path: 'reserva/cambiar/:tipo/sin-pago', element: <SinPago /> },
      { path: 'reserva/*', element: <Reserva /> },
      {
        path: 'checkin',
        element: <CheckinFlow />,
        children: [
          { index: true, element: <Ocupantes /> },
          { path: 'paso/:paso', element: <CheckinStep /> },
          { path: 'completar/:id', element: <Completar /> },
          { path: '*', element: <Ocupantes /> },
        ],
      },
      { path: 'salida', element: <Salida /> },
      { path: 'salida/despues', element: <Despues /> },
      { path: 'salida/*', element: <Salida /> },
      { path: 'avisos', element: <Avisos /> },
      { path: 'perfil', element: <Perfil /> },
      { path: 'accesos', element: <Accesos /> },
      { path: 'accesos/codigo', element: featureEnabled('accesosCodigo') ? <Codigo /> : <Accesos /> },
      { path: 'accesos/instrucciones', element: <Instrucciones /> },
      { path: 'accesos/como-llegar', element: <ComoLlegar /> },
      { path: 'accesos/clima', element: <Clima /> },
      { path: 'accesos/*', element: <Accesos /> },
      { path: 'guia', element: <Guia /> },
      { path: 'guia/*', element: <Guia /> },
      { path: 'ayuda', element: <Ayuda /> },
      { path: 'ayuda/peticiones/nueva', element: featureEnabled('ayudaPeticiones') ? <NuevaPeticion /> : <DisabledFeatureRoute /> },
      { path: 'ayuda/peticiones/:id', element: featureEnabled('ayudaPeticiones') ? <Peticion /> : <DisabledFeatureRoute /> },
      { path: 'ayuda/conversacion', element: featureEnabled('ayudaConversacion') ? <Conversacion /> : <DisabledFeatureRoute /> },
      { path: 'ayuda/*', element: <Ayuda /> },
      { path: '*', element: <TabPlaceholder titleKey="estancia" /> },
    ],
  },
]

export const router = createBrowserRouter(routes)
